import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router";
import { useBulkCreateFlow } from "../bulk-create-redesign/bulkCreateFlow";
import { useUnsavedProgressNavigationGuard } from "../hooks/useUnsavedProgressNavigationGuard";
import { FeatureFlag, useFeatureFlagValue } from "../../feature-flags";
import type { CompanyShellRow } from "../../generated/core-api";
import { useCompanyShellWorkloadsManager } from "../hooks";
import {
  CompanyProvisionWizardContext,
  INITIAL_WIZARD_STATE,
  type CompanyProvisionWizardState,
} from "./BulkCreateCompanyWizardContext";
import { UnsavedProgressConfirmModal } from "./UnsavedProgressConfirmModal";

const COMMIT_WORKFLOW_STORAGE_KEY =
  "pacific:onboarding:bulk-create:commit-workflow-id";

function initialWizardState(): CompanyProvisionWizardState {
  if (typeof window === "undefined") return INITIAL_WIZARD_STATE;
  try {
    const commitWorkflowId = window.sessionStorage.getItem(
      COMMIT_WORKFLOW_STORAGE_KEY
    );
    return commitWorkflowId
      ? { ...INITIAL_WIZARD_STATE, commitWorkflowId }
      : INITIAL_WIZARD_STATE;
  } catch {
    return INITIAL_WIZARD_STATE;
  }
}

function persistCommitWorkflowId(workflowId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (workflowId) {
      window.sessionStorage.setItem(COMMIT_WORKFLOW_STORAGE_KEY, workflowId);
    } else {
      window.sessionStorage.removeItem(COMMIT_WORKFLOW_STORAGE_KEY);
    }
  } catch {
    // A blocked storage API must not prevent the in-memory wizard from working.
  }
}

/**
 * Provides shared state to every screen under `/onboarding/bulk-create-company/*`.
 * Renders the current route via `<Outlet />` — mounted by `BulkCreateCompanyLayout`.
 *
 * Step 16 (LADR-039 / #440): `FeatureFlag.EnableWorkloadsCutover` switches
 * between the Track 1 (`useValidateCompanyShellProvision`/
 * `useCommitCompanyShellProvision`, called directly by the screens) and
 * Track 2 (`useCompanyShellWorkloadsManager`, Step 15) flows. Mirrors the
 * Step 9/13 wrapper-component precedent
 * (`WorkPatternImportInput.tsx`/`pay-component-import/components/WizardProvider.tsx`):
 * a plain `flag ? useHookA() : useHookB()` in one component would
 * conditionally call a different hook depending on a runtime value (a
 * Rules-of-Hooks violation, and would fire both tracks' API calls if both
 * were called unconditionally in one component). Instead, this top-level
 * component only calls `useFeatureFlagValue` and renders one of two child
 * components, each of which unconditionally calls its own track's hook(s).
 *
 * Track 2's manager is instantiated **here**, not in
 * `BulkCreateCompanyReviewScreen` — unlike Track 1's two stateless
 * invocation hooks (re-called with the full args every time; the resulting
 * `commitWorkflowId` is persisted separately so Results can resume polling
 * after navigation), Track 2's `runId`/`runStatus` live entirely inside the
 * one `useOnboardingMigrationRun` instance composed by the manager — there
 * is no caller-supplied correlation id to resume by (see that hook's module
 * doc). Since this component wraps every wizard screen via `<Outlet />`, the
 * same manager instance survives Review→Results navigation. It does **not**
 * survive a full page reload — a documented residual; Track 1's
 * `commitWorkflowId` + a fresh `useInvocationStatusPoll` call resumes across
 * a reload, Track 2 has no equivalent today.
 *
 * Both tracks share the unsaved-progress navigation guard added on `main`
 * (see `useUnsavedProgressNavigationGuard`): each wrapper component derives
 * its own `shouldBlockNavigation` from the state it owns (Track 1 from
 * `commitWorkflowId`, Track 2 from `shellManager.runId`, since Track 2 never
 * sets `commitWorkflowId`) and renders its own `<UnsavedProgressConfirmModal />`.
 */
export function BulkCreateCompanyWizardProvider() {
  const useWorkloadsCutover = useFeatureFlagValue(
    FeatureFlag.EnableWorkloadsCutover
  );

  return useWorkloadsCutover ? (
    <BulkCreateCompanyWizardProviderWorkloads />
  ) : (
    <BulkCreateCompanyWizardProviderTrackOne />
  );
}

/** Track 1 (flag off / default): no manager instance — screens call the two invocation hooks directly. */
function BulkCreateCompanyWizardProviderTrackOne() {
  const flow = useBulkCreateFlow();
  const [state, setState] =
    useState<CompanyProvisionWizardState>(initialWizardState);

  const updateState = useCallback(
    (partial: Partial<CompanyProvisionWizardState>) => {
      if (Object.hasOwn(partial, "commitWorkflowId")) {
        persistCommitWorkflowId(partial.commitWorkflowId ?? null);
      }
      setState((prev) => ({ ...prev, ...partial }));
    },
    []
  );
  const shouldBlockNavigation =
    state.rows.length > 0 && state.commitWorkflowId === null;
  const navigationGuard = useUnsavedProgressNavigationGuard(
    shouldBlockNavigation,
    [flow.uploadPath, flow.reviewPath, flow.resultsPath]
  );

  return (
    <CompanyProvisionWizardContext.Provider
      value={{
        state,
        updateState,
        shellManager: null,
        isNavigationBlocked: navigationGuard.isBlocked,
      }}
    >
      <Outlet />
      <UnsavedProgressConfirmModal
        isOpen={navigationGuard.isBlocked}
        onStay={navigationGuard.stay}
        onLeave={navigationGuard.leaveAnyway}
      />
    </CompanyProvisionWizardContext.Provider>
  );
}

/** A ref-held snapshot of the values `setPendingData` was last called with, to detect real changes. */
interface SeededSnapshot {
  rows: CompanyShellRow[] | null;
  configCode: string | null;
  configVersion: number | null;
  mypayEnvironment: CompanyProvisionWizardState["mypayEnvironment"];
  file: File | null;
}

const EMPTY_SNAPSHOT: SeededSnapshot = {
  rows: null,
  configCode: null,
  configVersion: null,
  mypayEnvironment: null,
  file: null,
};

/** Track 2 (flag on): owns the workloads manager for the whole wizard lifetime. */
function BulkCreateCompanyWizardProviderWorkloads() {
  const flow = useBulkCreateFlow();
  const [state, setState] = useState<CompanyProvisionWizardState>(
    INITIAL_WIZARD_STATE
  );

  const updateState = useCallback(
    (partial: Partial<CompanyProvisionWizardState>) => {
      setState((prev) => ({ ...prev, ...partial }));
    },
    []
  );

  const shellManager = useCompanyShellWorkloadsManager();

  /**
   * Seed the manager whenever Upload sets a new batch (a new file — `rows`
   * gets a fresh array reference) or the template/environment changes for
   * the batch already held (e.g. the user goes back to Upload and swaps the
   * template without re-uploading). This effect lives at the Provider level
   * (not in the Review screen) so it survives Review↔Upload round trips
   * without re-seeding on every Review mount — `setPendingData` fully resets
   * the manager's import/validate/run/patch pipeline, which would wipe an
   * in-progress or completed validate/commit if called spuriously.
   *
   * Compared by value (not `state.rows`' reference alone, and `template` by
   * its `config_code`/`version` rather than object identity, since
   * re-selecting the same template in Upload's `<select>` constructs a new
   * object) so a Review↔Upload round trip that changed nothing is a no-op.
   */
  const seededRef = useRef<SeededSnapshot>(EMPTY_SNAPSHOT);

  useEffect(() => {
    if (state.rows.length === 0) return;
    const seeded = seededRef.current;
    const unchanged =
      seeded.rows === state.rows &&
      seeded.configCode === (state.template?.config_code ?? null) &&
      seeded.configVersion === (state.template?.version ?? null) &&
      seeded.mypayEnvironment === state.mypayEnvironment &&
      seeded.file === state.file;
    if (unchanged) return;

    seededRef.current = {
      rows: state.rows,
      configCode: state.template?.config_code ?? null,
      configVersion: state.template?.version ?? null,
      mypayEnvironment: state.mypayEnvironment,
      file: state.file,
    };
    shellManager.setPendingData(
      state.rows,
      state.template,
      state.mypayEnvironment,
      state.file ?? undefined
    );
    // `shellManager` is a fresh object every render (the hook doesn't memoize
    // its return value), so it's included below to satisfy exhaustive-deps
    // rather than only `setPendingData` — harmless since this effect's body
    // is itself guarded by the `unchanged` check above and only calls
    // `setPendingData` when the seeded source values actually changed.
  }, [state.rows, state.template, state.mypayEnvironment, state.file, shellManager]);

  // Track 2 has no `commitWorkflowId` (see module doc) — a completed commit
  // is identified by `shellManager.runId` instead, so the guard must read
  // that rather than `state.commitWorkflowId` (which Track 2 never sets).
  const shouldBlockNavigation =
    state.rows.length > 0 && shellManager.runId === null;
  const navigationGuard = useUnsavedProgressNavigationGuard(
    shouldBlockNavigation,
    [flow.uploadPath, flow.reviewPath, flow.resultsPath]
  );

  return (
    <CompanyProvisionWizardContext.Provider
      value={{
        state,
        updateState,
        shellManager,
        isNavigationBlocked: navigationGuard.isBlocked,
      }}
    >
      <Outlet />
      <UnsavedProgressConfirmModal
        isOpen={navigationGuard.isBlocked}
        onStay={navigationGuard.stay}
        onLeave={navigationGuard.leaveAnyway}
      />
    </CompanyProvisionWizardContext.Provider>
  );
}
